import {
  formatMask,
  formatMonetaryToBr,
  formatToPercentBr,
  removeFormatMask,
} from '../../../../utils/formatMonetaryToBr'
import {CalculateDistract} from '../../../../coreGlobal/models/sale'

interface CancelSummaryProps {
  items: CalculateDistract
  residualValue: number
  totalAdjustedValue: number
  discount: number
  amountFine: number
  hasFine: boolean
  setDiscount: (value: number) => void
  totalPaidValue: number
  setTotalPaidValue: (value: number) => void
  setAmountFine: (value: number) => void
  setHasFine: (value: boolean) => void
  refundOption: number
  setRefundOption: (val: number) => void
  setModalOpen: (open: boolean) => void
  selectedItems: number[]
  handleSubmit: () => Promise<void>
  confirmCieloRefund: boolean
  setConfirmCieloRefund: (val: boolean) => void
}

const CancelSummary = ({
  items,
  residualValue,
  totalAdjustedValue,
  discount,
  amountFine,
  hasFine,
  setDiscount,
  totalPaidValue,
  setTotalPaidValue,
  setAmountFine,
  setHasFine,
  refundOption,
  setRefundOption,
  setModalOpen,
  selectedItems,
  handleSubmit,
  confirmCieloRefund,
  setConfirmCieloRefund,
}: CancelSummaryProps) => {
  const handleDiscountChange = (e: any) => {
    const value = Math.min(removeFormatMask(e.target.value), 20)
    setDiscount(value)
  }

  const handleFineChange = (e: any) => {
    const value = Math.min(removeFormatMask(e.target.value), 150)
    setAmountFine(value)
  }

  const handleTotalPaidChange = (e: any) => {
    const value = Math.min(removeFormatMask(e.target.value), items.saleAmount).toFixed(2)
    setTotalPaidValue(formatMask(value))
  }

  // 1. Preparacao dos dados
  const allItems = items.items || []
  const selectedIdsSet = new Set(selectedItems) // Transforma em Set para verificação rápida O(1)

  // Itens que NAO sao produtos (servicos/outros)
  const nonProductItems = allItems.filter((item) => item.productCategory !== 3)

  // 2. Regras para exibir o botao de pagamento

  // Usado para decidir se a tabela de parcelas restantes deve ser exibida
  const isAllNonProductsSelected =
    nonProductItems.length > 0 &&
    nonProductItems.every((item) => selectedIdsSet.has(item.saleItemId))

  const showPayButton = residualValue > 0

  // Distrato parcial com boleto parcelado: o saldo pode seguir nas parcelas pendentes do boleto
  const showKeepOnBoleto =
    !!items?.boleto &&
    !items?.recurrence &&
    items.items.length !== selectedItems.length &&
    residualValue > 0 &&
    !isAllNonProductsSelected

  const showDistractButton =
    !showPayButton &&
    selectedItems.length > 0 &&
    (residualValue === 0 || (residualValue < 0 && refundOption > 0))

  // Só o que foi capturado na Cielo é estornado automaticamente; o restante fica manual
  const refundAmount = residualValue * -1
  const cieloRefundAmount = Math.min(refundAmount, Number(items.cieloRefundableAmount))
  const manualRefundAmount = refundAmount - cieloRefundAmount

  return (
    <div className='mt-6'>
      <hr className='gray-300' />
      <h2 className='text-dark fw-bold text-hover-danger'>
        Valor Total da venda: {formatMonetaryToBr(items.saleAmount)}
      </h2>
      <hr className='gray-300' />
      <h2 className='text-dark fw-bold text-hover-danger'>
        Valor Total pago:
        {!items.hasOldRecurrence ? (
          ' ' + formatMask(totalPaidValue)
        ) : (
          <div className='row'>
            <div className='col-md-4 col-sm-6 mt-2'>
              <input
                type='text'
                className='form-control text-center fw-bold'
                value={formatMask(totalPaidValue)}
                onChange={handleTotalPaidChange}
              />
            </div>
          </div>
        )}
      </h2>
      <hr className='gray-300' />
      <h2 className='text-danger fw-bold'>
        {residualValue < 0 ? 'Valor a Restituir: ' : 'Valor Devedor: '}{' '}
        {'((' +
          formatMonetaryToBr(items?.saleAmount) +
          ' - ' +
          formatMonetaryToBr(totalAdjustedValue) +
          ') - ' +
          formatMask(totalPaidValue) +
          ') + ' +
          formatMonetaryToBr(hasFine ? amountFine : 0) +
          ' = ' +
          formatMonetaryToBr(residualValue * -1)}
      </h2>

      {items?.recurrence &&
        items.items.length !== selectedItems.length &&
        residualValue > 0 &&
        !isAllNonProductsSelected && (
          <>
            <hr className='gray-300' />
            <div className='py-5'>
              <table className='table table-row-dashed table-row-gray-300 gy-7'>
                <tbody>
                  <tr>
                    <td>
                      <b>Parcela Restante:</b>
                    </td>
                    <td>{items?.recurrence.unpaidInstallments}</td>
                    <td>
                      <b>Parcela Restante:</b>
                    </td>
                    <td>{items?.recurrence.unpaidInstallments}</td>
                  </tr>
                  <tr>
                    <td>
                      <b>Valor das Parcelas:</b>
                    </td>
                    <td>{formatMonetaryToBr(items?.recurrence.installmentValue)}</td>
                    <td>
                      <b>Valor das Parcelas Atualizada:</b>
                    </td>
                    <td>
                      {formatMonetaryToBr(residualValue / items?.recurrence.unpaidInstallments)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      {showKeepOnBoleto && items.boleto && (
        <>
          <hr className='gray-300' />
          <div className='py-5'>
            <table className='table table-row-dashed table-row-gray-300 gy-7'>
              <tbody>
                <tr>
                  <td>
                    <b>Parcelas de boleto pendentes:</b>
                  </td>
                  <td>{items.boleto.pendingInstallments}</td>
                  <td>
                    <b>Valor das Parcelas:</b>
                  </td>
                  <td>{formatMonetaryToBr(items.boleto.installmentValue)}</td>
                </tr>
                <tr>
                  <td colSpan={2}></td>
                  <td>
                    <b>Valor das Parcelas Atualizada:</b>
                  </td>
                  <td>{formatMonetaryToBr(residualValue / items.boleto.pendingInstallments)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
      <hr className='gray-300' />

      <div className='row'>
        <div className='col-md-4 col-sm-6'>
          <label className='fs-6 fw-bold'>Taxa ADM:</label>
          <input
            type='text'
            className='form-control text-center fw-bold'
            value={formatToPercentBr(discount)}
            onChange={handleDiscountChange}
          />

          <label className='fs-6 fw-bold mt-4'>Multa de Cancelamento:</label>
          <div className='d-flex mt-2'>
            <label className='custom-radio me-3'>
              <input type='radio' checked={hasFine} onChange={() => setHasFine(true)} />
              <span className='checkmark'></span> Sim
            </label>
            <label className='custom-radio'>
              <input type='radio' checked={!hasFine} onChange={() => setHasFine(false)} />
              <span className='checkmark'></span> Não
            </label>
          </div>

          {hasFine && (
            <input
              type='text'
              className='form-control text-center fw-bold mt-2'
              value={formatMonetaryToBr(amountFine)}
              onChange={handleFineChange}
            />
          )}

          {residualValue < 0 && (
            <>
              <label className='fs-6 fw-bold mt-4'>Forma de estorno:</label>
              <div className='d-flex mt-2'>
                <label className='custom-radio me-3'>
                  <input
                    type='radio'
                    checked={refundOption === 1}
                    onChange={() => setRefundOption(1)}
                  />
                  <span className='checkmark'></span>
                  Voucher
                </label>
                <label className='custom-radio'>
                  <input
                    type='radio'
                    checked={refundOption === 2}
                    onChange={() => setRefundOption(2)}
                  />
                  <span className='checkmark'></span>
                  Estorno
                </label>
              </div>

              {refundOption === 2 && items.hasCieloRefundEligiblePayment && (
                <div className='form-check mt-4'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='confirmCieloRefund'
                    checked={confirmCieloRefund}
                    onChange={(e) => setConfirmCieloRefund(e.target.checked)}
                  />
                  <label className='form-check-label fw-bold' htmlFor='confirmCieloRefund'>
                    Estornar {formatMonetaryToBr(cieloRefundAmount)} automaticamente via Cielo
                  </label>
                  {manualRefundAmount > 0 && (
                    <div className='text-muted mt-1'>
                      O valor restante de {formatMonetaryToBr(manualRefundAmount)} foi pago por
                      outras formas de pagamento e deverá ser estornado manualmente.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className='col-8 d-flex align-items-end justify-content-end mt-7'>
          {showKeepOnBoleto && (
            <button className='btn btn-light-danger me-3' onClick={handleSubmit}>
              Manter saldo nas parcelas do boleto
            </button>
          )}
          {showPayButton && (
            <button className='btn btn-danger' onClick={() => setModalOpen(true)}>
              Pagar
            </button>
          )}
          {showDistractButton && (
            <button className='btn btn-danger' onClick={handleSubmit}>
              Gerar Distrato
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default CancelSummary
